// Dynamic field-level edit permissions.
// The FieldPermissions table in the portal DB controls which roles can edit which fields.
// On app load, the frontend fetches this map via GET /api/admin/auth/fieldPermissions.
// To grant/revoke edit access, add/remove rows in FieldPermissions — no code deploy needed.
// To protect a new field, add canEditField("FieldName", userRoles, fieldPermissions, "PageName")
// to the component's JSX — this is the only code change required.
// example in SamplesModal.tsx SpecimenSource
//
// FieldPermissions table schema: ID (auto-increment), FieldName (varchar), RoleID (int)
// One row per role that should have edit access to a field.
// If a field has NO rows, it is editable by all roles (open by default).
//
// Page-level override:
// If you pass a pageName and the user has any role listed under that pageName,
// they can edit every field on that page regardless of field-specific rules.
// This is useful for roles like Global Admin that should edit everything.
//
// SQL to grant a role edit access to a field:
//   INSERT INTO FieldPermissions (FieldName, RoleID) VALUES ('FieldName', RoleID);
//
// Example — allow RoleID 1 and 9 to edit SpecimenSource:
//   INSERT INTO FieldPermissions (FieldName, RoleID) VALUES ('SpecimenSource', 1);
//   INSERT INTO FieldPermissions (FieldName, RoleID) VALUES ('SpecimenSource', 9);
//
// Example — allow RoleID 1 (Global Admin) to edit every field on SamplesModal:
//   INSERT INTO FieldPermissions (FieldName, RoleID) VALUES ('SamplesModal', 1);
//
// Works with custom fields too — use the custom field's DisplayName as FieldName:
//   INSERT INTO FieldPermissions (FieldName, RoleID) VALUES ('Tracking #', 1);
//
// To revoke access:
//   DELETE FROM FieldPermissions WHERE FieldName = 'SpecimenSource' AND RoleID = 9;

export type FieldPermissions = Record<string, number[]>;

/**
 * Parse the UserRoles JSON string into an array of role IDs.
 * e.g. "[1]" => [1], "[1,2]" => [1, 2]
 */
export function parseUserRoles(userRoles: string | null | undefined): number[] {
  if (!userRoles) return [];
  try {
    const parsed = JSON.parse(userRoles);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

/**
 * Returns true if the user has a role listed under the pageName entry.
 * Grants page-level edit access that overrides field-specific restrictions.
 * Useful for wrapping existing readOnly/disabled logic:
 *   readOnly={!hasPageOverride && !this.props.isGlobal}
 */
export function hasPageOverride(
  pageName: string,
  userRoles: string | number[],
  fieldPermissions: FieldPermissions | null | undefined
): boolean {
  if (!fieldPermissions || !fieldPermissions[pageName]) return false;
  const roles = Array.isArray(userRoles) ? userRoles : parseUserRoles(userRoles);
  return roles.some((role) => fieldPermissions[pageName].includes(role));
}

/**
 * Check if any of the user's roles allow editing a specific field.
 * If a pageName is provided and the user has a role listed under that page,
 * they can edit every field on the page regardless of field-specific rules.
 * If the field is not in the permissions map, it is editable by all roles.
 * If permissions haven't loaded yet, defaults to editable.
 */
export function canEditField(
  fieldName: string,
  userRoles: string | number[],
  fieldPermissions: FieldPermissions | null | undefined,
  pageName?: string
): boolean {
  if (!fieldPermissions) return true;

  const roles = Array.isArray(userRoles) ? userRoles : parseUserRoles(userRoles);

  // Page-level override: if the user has any role listed under the pageName,
  // grant full edit access to the page.
  if (pageName && fieldPermissions[pageName]) {
    if (roles.some((role) => fieldPermissions[pageName].includes(role))) {
      return true;
    }
  }

  const allowedRoles = fieldPermissions[fieldName];
  if (!allowedRoles) return true;

  return roles.some((role) => allowedRoles.includes(role));
}

/**
 * Returns the readOnly value for a field based on permissions.
 * - undefined: field has no entry in permissions → use default behavior (e.g. patientSearchReadOnly)
 * - false: field has entry AND user has access → editable
 * - true: field has entry AND user does NOT have access → read-only
 * If a pageName is provided and the user has page-level access, always returns false.
 */
export function getFieldReadOnly(
  fieldName: string,
  userRoles: string | number[],
  fieldPermissions: FieldPermissions | null | undefined,
  pageName?: string
): boolean | undefined {
  if (!fieldPermissions) return undefined;

  // Page-level override grants edit access regardless of field entry
  if (pageName && fieldPermissions[pageName]) {
    const roles = Array.isArray(userRoles) ? userRoles : parseUserRoles(userRoles);
    if (roles.some((role) => fieldPermissions[pageName].includes(role))) {
      return false;
    }
  }

  if (!(fieldName in fieldPermissions)) return undefined;
  return !canEditField(fieldName, userRoles, fieldPermissions, pageName);
}
